<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { T } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { ExtraSpinCounts } from '@/types/fortune-wheel'
import { computed, ref, watch } from 'vue'
import { useConfig } from '@/composables/game/config'
import { useNow } from '@/composables/game/time'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { formatDuration } from '@/helpers/date'
import FortuneWheelImage from '@/assets/images/fortune-wheel.webp'
import api from '@/services/api'
import CrossCircleIcon2 from '@/components/icons/CrossCircleIcon2.vue'
import ShopItemDetailsModal from '@/components/modals/ShopItemDetailsModal.vue'
import ShopPurchaseSuccessModal from '@/components/modals/ShopPurchaseSuccessModal.vue'

const props = defineProps<{
	show: boolean,
	spinUpgrades: ExtraSpinCounts
}>()

const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'purchaseDone'): void
}>()

const { config } = useConfig()
const { counterToNextDay } = useNow()
const { getStarPrice } = useEcommercePrice()

const item = computed(() => config.value.game.ecommerce.items.find(item => item.id === 'spin.10.50'))
const itemStarPrice = computed(() => item.value ? getStarPrice(item.value) : null)

const dailySpinsCount = computed(() => config.value.game.fortuneWheel.extraSpin.daily.amount)
const countdown = computed(() => formatDuration(counterToNextDay.value))

const showPurchaseModal = ref(false)
const showSuccessModal = ref(false)

watch(() => props.show, (val) => {
  if (!val) return
  api.trackEvent({
    event: 'showOutOfSpinModal',
    metadata: {},
  })
}, { immediate: true })

function handlePurchase() {
  showPurchaseModal.value = false
  showSuccessModal.value = true
  emit('hide')
  emit('purchaseDone')
}

function handleSuccessClose() {
  showSuccessModal.value = false
}
</script>
<template>
  <TheBottomSheet
    v-if="item"
    :show="show"
    centered
    close-button-class="z-[10] absolute -top-1.5 -right-1.5"
    outer-content-class="overflow-visible rounded-t-[24px] rounded-b-[24px] bg-transparent"
    border-container-class="overflow-visible rounded-t-[24px] rounded-b-[24px]"
    base-modal-class="overflow-visible"
    content-class="py-8 [background-image:url('@/assets/images/modal-bg-gradient-blue-pink.webp')] rounded-t-[22px] rounded-b-[22px]"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <CrossCircleIcon2 class="limited-pack-close-icon size-8" />
    </template>
    <div
      class="absolute inset-1 rounded-[22px] bg-[linear-gradient(0deg,_rgb(0_0_0)_0%,_rgb(0_0_0_/_0.02)_80%,_rgb(0_0_0_/_0)_100%)] z-[1]"
    />
    <div class="flex flex-col items-center relative z-[2]">
      <h2
        class="text-center text-light-white text-[36px] leading-[42px] font-black font-pilat text-shadow-2"
      >
        <T
          key-name="fortune_wheel.info.out_of_spins.title"
          default-value="Out of spins?"
        />
      </h2>
      <p class="text-white/60 text-[18px] leading-6 text-center font-semibold">
        Top up now to progress faster!
      </p>

      <div class="mt-8 flex flex-col items-center mb-3">
        <img
          :src="FortuneWheelImage"
          class="w-[135px] h-auto filter drop-shadow-[0_0_24px_rgba(0,0,0,0.62)]"
        >
        <span
          class="text-center uppercase font-bold text-[#671DFB] text-[69px] leading-[93px] [-webkit-text-stroke-width:14px] [-webkit-text-stroke-color:#36EEFF] [paint-order:stroke_fill] -mt-10 [filter:drop-shadow(0_0_18px_#10F)]"
        >
          {{ `+${item.benefits[0].amount}` }}
        </span>
      </div>
      <BaseButton
        v-if="itemStarPrice"
        style-type="primary-pink"
        class="!px-[42px] !py-4 !text-[24px] !leading-[28] !tracking-[1px] !font-bold"
        @click="showPurchaseModal = true"
      >
        ${{ itemStarPrice.usdAmount }}
      </BaseButton>

      <p
        class="mt-6 text-center flex items-center flex-wrap gap-x-1.5 text-white/[.87] text-base font-semibold text-shadow-2"
      >
        <T
          key-name="fortune_wheel.out_of_spins.or_wait"
          default-value="Or wait"
        />
        <span
          class="font-black text-black font-pilat [-webkit-text-stroke-width:4px] [-webkit-text-stroke-color:#FFF] [paint-order:stroke_fill] min-w-[86px] -mt-1"
        >
          {{ ` ${countdown} ` }}
        </span>
        <T
          key-name="fortune_wheel.out_of_spins.for_spins"
          default-value="for {count, plural, one {# spin} other {# spins}}"
          :params="{count: dailySpinsCount}"
        />
      </p>
    </div>
  </TheBottomSheet>

  <ShopItemDetailsModal
    v-if="item && showPurchaseModal"
    :item="item"
    :show="showPurchaseModal"
    @hide="showPurchaseModal = false"
    @on-purchase="handlePurchase"
  >
    <template #itemIcon>
      <div class="flex flex-col items-center">
        <img
          :src="FortuneWheelImage"
          class="w-[135px] h-auto filter drop-shadow-[0_0_24px_rgba(0,0,0,0.62)]"
        >
        <span
          class="text-center uppercase font-bold text-[#671DFB] text-[69px] leading-[93px] [-webkit-text-stroke-width:14px] [-webkit-text-stroke-color:#36EEFF] [paint-order:stroke_fill] -mt-10 [filter:drop-shadow(0_0_18px_#10F)]"
        >
          {{ `+${item.benefits[0].amount}` }}
        </span>
      </div>
    </template>
  </ShopItemDetailsModal>

  <ShopPurchaseSuccessModal
    v-if="item && showSuccessModal"
    :item="item"
    :show="showSuccessModal"
    @hide="handleSuccessClose"
  >
    <template #itemIcon>
      <div class="flex flex-col items-center">
        <img
          :src="FortuneWheelImage"
          class="w-[135px] h-auto filter drop-shadow-[0_0_24px_rgba(0,0,0,0.62)]"
        >
        <span
          class="text-center uppercase font-bold text-[#671DFB] text-[69px] leading-[93px] [-webkit-text-stroke-width:14px] [-webkit-text-stroke-color:#36EEFF] [paint-order:stroke_fill] -mt-10 [filter:drop-shadow(0_0_18px_#10F)]"
        >
          {{ `+${item.benefits[0].amount}` }}
        </span>
      </div>
    </template>
  </ShopPurchaseSuccessModal>
</template>
<style scoped>
.glass-effect-container {
	position: relative;
	background: rgba(0, 0, 0, 0.06);
	backdrop-filter: blur(16px);
	border-radius: 28px;
	box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
	-webkit-backdrop-filter: blur(6px);

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 28px;
		padding: 1px;
		background: linear-gradient(45deg, rgba(255, 255, 255, 0.66) 0%, rgba(153, 153, 153, 0.32) 100%);
		-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
	}
}
</style>
